// Admin Team & Roles Management (Super Admin only)
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetTitle, SheetDescription, SheetFooter,
} from '../../components/ui/sheet';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, UserPlus, Shield, Search, ChevronLeft, ChevronRight,
  Lock, RefreshCw, Mail, Users, BookOpen, Tag, Video, HelpCircle, DollarSign,
  TrendingUp, FileText, BarChart3, Award, Image, Share2, Megaphone, Radio, Key,
  Settings, Receipt, Eye, EyeOff, Phone, Globe, CreditCard,
} from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const EMPTY_ROLE_FORM = { name: '', description: '', permissions: [] };
const EMPTY_ADMIN_FORM = { name: '', email: '', password: '', role: '', send_welcome_email: false };
const ADMINS_PAGE_SIZE = 20;
const PASSWORD_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';

// Mirrors the real admin sidebar order/icons (`allMenuItems` in AdminPages.js) —
// only the permission-gated entries, since those are the only ones a custom
// role's permissions can ever affect (Dashboard/Team & Roles/Backup are not).
const SIDEBAR_PREVIEW_ITEMS = [
  { label: 'Users', permission: 'manage_users', icon: Users },
  { label: 'CRM & Leads', permission: 'manage_crm', icon: Phone },
  { label: 'Courses', permission: 'manage_courses', icon: BookOpen },
  { label: 'Categories', permission: 'manage_courses', icon: Tag },
  { label: 'Lessons', permission: 'manage_lessons', icon: Video },
  { label: 'Quizzes', permission: 'manage_quizzes', icon: HelpCircle },
  { label: 'Coupons', permission: 'manage_coupons', icon: DollarSign },
  { label: 'Revenue', permission: 'view_revenue', icon: TrendingUp },
  { label: 'Orders & Invoices', permission: 'view_revenue', icon: FileText },
  { label: 'Video Analytics', permission: 'view_analytics', icon: BarChart3 },
  { label: 'Certificates', permission: 'manage_certificates', icon: Award },
  { label: 'Projects', permission: 'manage_projects', icon: Image },
  { label: 'Affiliates', permission: 'manage_affiliates', icon: Share2 },
  { label: 'Share Rewards', permission: 'manage_marketing', icon: Megaphone },
  { label: 'Live Classes', permission: 'manage_live_classes', icon: Radio },
  { label: 'Email', permission: 'send_notifications', icon: Mail },
  { label: 'Integrations', permission: 'manage_integrations', icon: Key },
  { label: 'Payment Gateways', permission: 'manage_integrations', icon: CreditCard },
  { label: 'Settings', permission: 'manage_settings', icon: Settings },
  { label: 'Country Pricing', permission: 'manage_settings', icon: Globe },
  { label: 'Billing & Tax', permission: 'manage_settings', icon: Receipt },
  { label: 'GST & Billing (India)', permission: 'manage_settings', icon: Receipt },
];

const generatePassword = () => {
  let pwd = '';
  for (let i = 0; i < 12; i++) pwd += PASSWORD_CHARS[Math.floor(Math.random() * PASSWORD_CHARS.length)];
  return pwd;
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const chars = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return chars.toUpperCase();
};

const roleBadgeClass = (role) => {
  if (role === 'super_admin') return 'bg-emerald-700 text-white hover:bg-emerald-700 border-transparent';
  return 'bg-[#A67C52] text-white hover:bg-[#A67C52] border-transparent';
};

export const AdminTeamRoles = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const headers = { Authorization: `Bearer ${token}` };

  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState({});
  const [permissionLabels, setPermissionLabels] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [menuMap, setMenuMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showHiddenInPreview, setShowHiddenInPreview] = useState(true);

  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN_FORM);
  const [autoGeneratePassword, setAutoGeneratePassword] = useState(false);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState(EMPTY_ROLE_FORM);

  const [adminSearch, setAdminSearch] = useState('');
  const [adminPage, setAdminPage] = useState(1);
  const [removeTarget, setRemoveTarget] = useState(null);

  const [activityLogs, setActivityLogs] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityAdminFilter, setActivityAdminFilter] = useState('all');
  const [activityStart, setActivityStart] = useState('');
  const [activityEnd, setActivityEnd] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [adminsRes, rolesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/admins`, { headers }),
        axios.get(`${API_URL}/admin/roles`, { headers }),
        axios.get(`${API_URL}/admin/permissions`, { headers }),
      ]);
      setAdmins(Array.isArray(adminsRes.data) ? adminsRes.data : []);
      setRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setCategories(permsRes.data?.categories || {});
      setPermissionLabels(permsRes.data?.labels || {});
      setAllPermissions(permsRes.data?.permissions || []);
      setMenuMap(permsRes.data?.menu_map || {});
    } catch (err) {
      console.error(err);
      toast.error(t('admin_team_load_fail', 'Failed to load team data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchActivityLog = async () => {
    try {
      const params = { page: activityPage, limit: 100 };
      if (activityAdminFilter !== 'all') params.admin_id = activityAdminFilter;
      if (activityStart) params.start_date = activityStart;
      if (activityEnd) params.end_date = activityEnd;
      const res = await axios.get(`${API_URL}/admin/activity-log`, { headers, params });
      setActivityLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
      setActivityTotal(res.data?.total || 0);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchActivityLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activityPage, activityAdminFilter, activityStart, activityEnd]);

  const roleOptions = ['super_admin', ...roles.map((r) => r.name)];

  // ---- Admin Users ----

  const filteredAdmins = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter(
      (a) => a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
    );
  }, [admins, adminSearch]);

  const adminTotalPages = Math.max(1, Math.ceil(filteredAdmins.length / ADMINS_PAGE_SIZE));
  const paginatedAdmins = useMemo(
    () => filteredAdmins.slice((adminPage - 1) * ADMINS_PAGE_SIZE, adminPage * ADMINS_PAGE_SIZE),
    [filteredAdmins, adminPage]
  );

  useEffect(() => {
    setAdminPage(1);
  }, [adminSearch]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!adminForm.role) {
      toast.error(t('admin_team_select_role', 'Please select a role'));
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/admins`, adminForm, { headers });
      toast.success(t('admin_team_admin_created', 'Admin created'));
      setAdminDialogOpen(false);
      setAdminForm(EMPTY_ADMIN_FORM);
      setAutoGeneratePassword(false);
      fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_team_admin_save_fail', 'Failed to create admin'));
    }
  };

  const toggleAutoGenerate = (checked) => {
    setAutoGeneratePassword(checked);
    setAdminForm((prev) => ({ ...prev, password: checked ? generatePassword() : '' }));
  };

  const handleChangeRole = async (adminUser, newRole) => {
    if (newRole === adminUser.role) return;
    try {
      await axios.put(`${API_URL}/admin/admins/${adminUser.id}/role`, { role: newRole }, { headers });
      toast.success(t('admin_team_role_updated', 'Role updated'));
      fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_team_role_update_fail', 'Failed to update role'));
    }
  };

  const handleRemoveAccess = async () => {
    if (!removeTarget) return;
    try {
      await axios.delete(`${API_URL}/admin/admins/${removeTarget.id}`, { headers });
      toast.success(t('admin_team_access_removed', 'Admin access removed'));
      setRemoveTarget(null);
      fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_team_remove_fail', 'Failed to remove access'));
    }
  };

  // ---- Roles ----

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm(EMPTY_ROLE_FORM);
    setRoleDialogOpen(true);
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
    setRoleDialogOpen(true);
  };

  const togglePermission = (permission) => {
    setRoleForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const isCategoryFullySelected = (perms) => perms.length > 0 && perms.every((p) => roleForm.permissions.includes(p));

  const toggleCategoryAll = (perms) => {
    const allSelected = isCategoryFullySelected(perms);
    setRoleForm((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !perms.includes(p))
        : Array.from(new Set([...prev.permissions, ...perms])),
    }));
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.put(`${API_URL}/admin/roles/${editingRole.id}`, {
          description: roleForm.description,
          permissions: roleForm.permissions,
        }, { headers });
        toast.success(t('admin_team_role_saved', 'Role updated'));
      } else {
        await axios.post(`${API_URL}/admin/roles`, roleForm, { headers });
        toast.success(t('admin_team_role_created', 'Role created'));
      }
      setRoleDialogOpen(false);
      setEditingRole(null);
      setRoleForm(EMPTY_ROLE_FORM);
      fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_team_role_save_fail', 'Failed to save role'));
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.admin_count > 0) {
      toast.error(t('admin_team_role_in_use', 'Cannot delete a role that is still assigned to admin users'));
      return;
    }
    if (!window.confirm(t('admin_team_confirm_delete_role', 'Delete this role?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/roles/${role.id}`, { headers });
      toast.success(t('admin_team_role_deleted', 'Role deleted'));
      fetchData();
      fetchActivityLog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_team_role_delete_fail', 'Failed to delete role'));
    }
  };

  const superAdminCount = admins.filter((a) => a.role === 'super_admin').length;

  return (
    <div data-testid="admin-team-roles">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">{t('admin_page_team', 'Team & Roles')}</h1>
        <p className="text-muted-foreground">
          {t('admin_team_subtitle', 'Manage admin users and the permissions assigned to custom roles')}
        </p>
      </div>

      <Tabs defaultValue="admins">
        <TabsList>
          <TabsTrigger value="admins" data-testid="team-tab-admins">
            {t('admin_team_tab_admins', 'Admin Users')}
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="team-tab-roles">
            {t('admin_team_tab_roles', 'Roles')}
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="team-tab-activity">
            {t('admin_team_tab_activity', 'Activity Log')}
          </TabsTrigger>
        </TabsList>

        {/* ---- Admin Users Tab ---- */}
        <TabsContent value="admins">
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('admin_team_search_ph', 'Search by name or email...')}
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
            <Dialog open={adminDialogOpen} onOpenChange={(open) => {
              setAdminDialogOpen(open);
              if (!open) { setAdminForm(EMPTY_ADMIN_FORM); setAutoGeneratePassword(false); }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-admin-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('admin_team_add_admin', 'Add Admin')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('admin_team_add_admin', 'Add Admin')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div>
                    <Label htmlFor="admin-name">{t('admin_team_name', 'Full Name')}</Label>
                    <Input
                      id="admin-name"
                      value={adminForm.name}
                      onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-email">{t('admin_team_email', 'Email')}</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="admin-password">{t('admin_team_password', 'Password')}</Label>
                      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox checked={autoGeneratePassword} onCheckedChange={toggleAutoGenerate} />
                        {t('admin_team_auto_generate', 'Auto-generate')}
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        id="admin-password"
                        type={autoGeneratePassword ? 'text' : 'password'}
                        value={adminForm.password}
                        onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                        disabled={autoGeneratePassword}
                        required
                      />
                      {autoGeneratePassword && (
                        <Button type="button" variant="outline" size="icon" onClick={() => toggleAutoGenerate(true)}>
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>{t('admin_team_role', 'Role')}</Label>
                    <Select value={adminForm.role} onValueChange={(val) => setAdminForm({ ...adminForm, role: val })}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('admin_team_select_role', 'Select a role')} />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={adminForm.send_welcome_email}
                      onCheckedChange={(v) => setAdminForm({ ...adminForm, send_welcome_email: !!v })}
                    />
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {t('admin_team_send_welcome', 'Send welcome email')}
                  </label>
                  <DialogFooter>
                    <Button type="submit" data-testid="save-admin-button">
                      {t('admin_save', 'Save')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>{t('admin_team_name', 'Name')}</TableHead>
                    <TableHead>{t('admin_team_email', 'Email')}</TableHead>
                    <TableHead>{t('admin_team_role', 'Role')}</TableHead>
                    <TableHead>{t('admin_team_permissions_count', 'Permissions')}</TableHead>
                    <TableHead>{t('admin_team_created', 'Created')}</TableHead>
                    <TableHead>{t('admin_team_last_login', 'Last Login')}</TableHead>
                    <TableHead className="text-right">{t('admin_team_actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{t('admin_loading', 'Loading...')}</TableCell></TableRow>
                  ) : admins.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{t('admin_team_no_admins', 'No team members added yet — Add your first admin')}</TableCell></TableRow>
                  ) : filteredAdmins.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">{t('admin_team_no_search_results', 'No admins match your search')}</TableCell></TableRow>
                  ) : (
                    paginatedAdmins.map((a) => {
                      const isSelf = a.id === user?.id;
                      return (
                        <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                          <TableCell>
                            <div className="w-8 h-8 rounded-full bg-[#A67C52]/15 text-[#A67C52] flex items-center justify-center text-xs font-bold">
                              {initials(a.name)}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{a.name}</TableCell>
                          <TableCell>{a.email}</TableCell>
                          <TableCell>
                            <Badge className={roleBadgeClass(a.role)}>
                              {a.role === 'super_admin' && <Shield className="w-3 h-3 mr-1" />}
                              {a.role}
                            </Badge>
                          </TableCell>
                          <TableCell>{a.role === 'super_admin' ? allPermissions.length : (a.permissions_count ?? 0)}</TableCell>
                          <TableCell>{a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>{a.last_login ? new Date(a.last_login).toLocaleString() : '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {a.role !== 'super_admin' && (
                                <Select
                                  value={a.role}
                                  onValueChange={(val) => handleChangeRole(a, val)}
                                  disabled={isSelf}
                                >
                                  <SelectTrigger className="w-36 h-8 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {roleOptions.map((r) => (
                                      <SelectItem key={r} value={r}>{r}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => setRemoveTarget(a)}
                                disabled={isSelf}
                                data-testid={`remove-admin-${a.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                {t('admin_team_remove_access', 'Remove Admin')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {filteredAdmins.length > ADMINS_PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {t('admin_team_page_of', `Page ${adminPage} of ${adminTotalPages}`)}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={adminPage <= 1} onClick={() => setAdminPage((p) => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={adminPage >= adminTotalPages} onClick={() => setAdminPage((p) => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ---- Roles Tab (cards) ---- */}
        <TabsContent value="roles">
          <div className="flex items-center justify-end mb-4">
            <Button size="sm" onClick={openCreateRole} data-testid="create-role-button">
              <Plus className="w-4 h-4 mr-2" />
              {t('admin_team_create_role', 'Create New Role')}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Locked super_admin card */}
            <Card className="border-emerald-700/30">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    super_admin
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                  </h3>
                  <Badge variant="secondary" className="text-xs">{t('admin_team_system_role', 'System role')}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('admin_team_super_admin_desc', 'Full access to every permission and admin action.')}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{allPermissions.length} {t('admin_team_permissions_label', 'permissions')}</span>
                  <span>{superAdminCount} {t('admin_team_admins_label', 'admins')}</span>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <Card><CardContent className="p-5 text-muted-foreground text-sm">{t('admin_loading', 'Loading...')}</CardContent></Card>
            ) : roles.length === 0 ? null : (
              roles.map((role) => (
                <Card key={role.id} data-testid={`role-card-${role.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-lg">{role.name}</h3>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditRole(role)} data-testid={`edit-role-${role.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleDeleteRole(role)}
                          disabled={(role.admin_count ?? 0) > 0}
                          data-testid={`delete-role-${role.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 min-h-[1.25rem]">{role.description || '-'}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{(role.permissions || []).length} {t('admin_team_permissions_label', 'permissions')}</span>
                      <span>{role.admin_count ?? 0} {t('admin_team_admins_label', 'admins')}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {!loading && roles.length === 0 && (
              <Card className="md:col-span-2 lg:col-span-2">
                <CardContent className="p-5 text-muted-foreground text-sm text-center">
                  {t('admin_team_no_roles', 'No custom roles yet — create one to get started')}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ---- Activity Log Tab ---- */}
        <TabsContent value="activity">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Select value={activityAdminFilter} onValueChange={(v) => { setActivityAdminFilter(v); setActivityPage(1); }}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('admin_team_all_admins', 'All Admins')}</SelectItem>
                {admins.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={activityStart}
              onChange={(e) => { setActivityStart(e.target.value); setActivityPage(1); }}
              className="w-40"
            />
            <span className="text-muted-foreground text-sm">{t('admin_team_to', 'to')}</span>
            <Input
              type="date"
              value={activityEnd}
              onChange={(e) => { setActivityEnd(e.target.value); setActivityPage(1); }}
              className="w-40"
            />
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin_team_activity_admin', 'Admin')}</TableHead>
                    <TableHead>{t('admin_team_activity_action', 'Action')}</TableHead>
                    <TableHead>{t('admin_team_activity_when', 'When')}</TableHead>
                    <TableHead>{t('admin_team_activity_ip', 'IP Address')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLogs.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{t('admin_team_no_activity', 'No activity recorded yet')}</TableCell></TableRow>
                  ) : (
                    activityLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.admin_email}</TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{log.ip_address || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {activityTotal > 100 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {t('admin_team_page_of', `Page ${activityPage} of ${Math.max(1, Math.ceil(activityTotal / 100))}`)}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={activityPage <= 1} onClick={() => setActivityPage((p) => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={activityPage >= Math.ceil(activityTotal / 100)} onClick={() => setActivityPage((p) => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ---- Remove Admin Confirmation ---- */}
      <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin_team_confirm_remove_title', 'Remove admin access?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin_team_confirm_remove', 'Remove admin access for this user?')} {removeTarget?.email}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin_cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveAccess} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('admin_team_remove_access', 'Remove Admin')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---- Create/Edit Role Drawer ---- */}
      <Sheet open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <SheetContent side="right" className="w-full sm:max-w-none sm:w-[720px] md:w-[860px] p-0 flex flex-col">
          <form onSubmit={handleSaveRole} className="flex flex-col h-full min-h-0">
            <div className="p-6 pb-4 border-b">
              <SheetTitle>
                {editingRole ? t('admin_team_edit_role', 'Edit Role') : t('admin_team_create_role', 'Create New Role')}
              </SheetTitle>
              <SheetDescription>
                {t('admin_team_role_drawer_desc', 'Choose the permissions this role grants to any admin assigned to it.')}
              </SheetDescription>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6 mb-6">
                <div>
                  <Label htmlFor="role-name">{t('admin_team_role_name', 'Name')}</Label>
                  <Input
                    id="role-name"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    disabled={!!editingRole}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="role-description">{t('admin_team_role_description', 'Description')}</Label>
                  <Textarea
                    id="role-description"
                    value={roleForm.description}
                    onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                {/* Left: permission checkboxes, grouped by category */}
                <div className="md:w-[55%]">
                  <Label className="mb-2 block">{t('admin_team_permissions', 'Permissions')}</Label>
                  <div className="space-y-5">
                    {Object.entries(categories).map(([category, perms]) => (
                      <div key={category}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-muted-foreground">{category}</p>
                          <button
                            type="button"
                            className="text-xs text-[#A67C52] hover:underline"
                            onClick={() => toggleCategoryAll(perms)}
                          >
                            {isCategoryFullySelected(perms)
                              ? t('admin_team_deselect_all', 'Deselect All')
                              : t('admin_team_select_all', 'Select All')}
                          </button>
                        </div>
                        <div className="space-y-3">
                          {perms.map((perm) => {
                            const menus = menuMap[perm] || [];
                            return (
                              <div key={perm}>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                  <Checkbox
                                    checked={roleForm.permissions.includes(perm)}
                                    onCheckedChange={() => togglePermission(perm)}
                                    data-testid={`perm-checkbox-${perm}`}
                                  />
                                  {permissionLabels[perm] || perm}
                                </label>
                                <div className="flex items-center flex-wrap gap-1 mt-1 ml-6">
                                  <span className="text-xs text-muted-foreground">
                                    {t('admin_team_perm_sidebar_arrow', '→ Sidebar:')}
                                  </span>
                                  {menus.length === 0 ? (
                                    <span className="text-xs text-muted-foreground italic">
                                      {t('admin_team_perm_no_sidebar', 'no sidebar menu')}
                                    </span>
                                  ) : (
                                    menus.map((menu) => (
                                      <Badge
                                        key={menu}
                                        variant="secondary"
                                        className="text-[10px] font-normal pointer-events-none"
                                      >
                                        {menu}
                                      </Badge>
                                    ))
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: live sidebar preview */}
                <div className="md:w-[45%]">
                  <div className="sticky top-0 border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium">{t('admin_team_sidebar_preview', 'Sidebar Preview')}</p>
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setShowHiddenInPreview((v) => !v)}
                      >
                        {showHiddenInPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {showHiddenInPreview
                          ? t('admin_team_preview_hide_hidden', 'Hide unselected')
                          : t('admin_team_preview_show_hidden', 'Show unselected')}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {t('admin_team_preview_super_admin_note', 'Super Admin sees everything, regardless of this role’s permissions.')}
                    </p>
                    <div className="space-y-1">
                      {SIDEBAR_PREVIEW_ITEMS.filter((item) => (menuMap[item.permission] || []).includes(item.label)).map((item) => {
                        const Icon = item.icon;
                        const active = roleForm.permissions.includes(item.permission);
                        if (!active && !showHiddenInPreview) return null;
                        return (
                          <div
                            key={item.label}
                            className={`flex items-center gap-2 text-sm rounded px-2 py-1 ${
                              active ? 'text-foreground' : 'text-muted-foreground/50 line-through'
                            }`}
                            data-testid={`sidebar-preview-${item.permission}-${item.label}`}
                          >
                            <Icon className="w-4 h-4 shrink-0" />
                            <span>{item.label}</span>
                            {!active && (
                              <span className="text-[10px] not-italic no-underline">
                                {t('admin_team_preview_hidden_suffix', '(hidden)')}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {roleForm.permissions.length === 0 && (
                        <p className="text-xs text-muted-foreground italic pt-2">
                          {t('admin_team_preview_empty', 'No sidebar menus visible with the current selection.')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <SheetFooter className="p-6 border-t">
              <Button type="button" variant="outline" onClick={() => setRoleDialogOpen(false)}>
                {t('admin_cancel', 'Cancel')}
              </Button>
              <Button type="submit" data-testid="save-role-button">
                {t('admin_save', 'Save')}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
};
